/**
 * Wrap an action so anonymous users get the auth modal instead of running it.
 *
 *   const requireAuth = useRequireAuth();
 *   <Button onClick={() => requireAuth(handleGenerate, { intentLabel: 'generate this summary' })}>
 *
 * If the user is signed in, the action runs immediately. If they're
 * anonymous, the modal opens with a tailored headline and the action
 * is stashed in localStorage so it can be resumed after the magic
 * link sign-in (handled by the page's resume listener — see Phase 3).
 */

import { useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';

export interface RequireAuthOptions {
  /** Human-readable verb like "follow this docket" or "generate this summary". */
  intentLabel?: string;
  /** Custom headline override (takes precedence over intentLabel). */
  headline?: string;
  /** Stable id stashed in localStorage so a page-level resume handler can act on it. */
  intentKey?: string;
  /** Arbitrary JSON payload to persist with the intent. */
  intentPayload?: unknown;
}

const PENDING_INTENT_KEY = 'cg.pending_action_intent';

export function useRequireAuth() {
  const { user, openAuthModal } = useAuth();

  return useCallback(
    (action: () => void | Promise<void>, opts: RequireAuthOptions = {}) => {
      if (user) {
        return action();
      }

      // Stash for Phase 3 auto-resume. We always write, even when no
      // intentKey is supplied, because a page might still want to know
      // the user *tried* to do something on this route.
      try {
        localStorage.setItem(
          PENDING_INTENT_KEY,
          JSON.stringify({
            intentKey: opts.intentKey ?? null,
            intentLabel: opts.intentLabel ?? null,
            payload: opts.intentPayload ?? null,
            route: window.location.pathname + window.location.search,
            ts: Date.now(),
          })
        );
      } catch {
        // localStorage might be unavailable (private mode, strict
        // browsers). The modal still opens — we just lose auto-resume.
      }

      const headline =
        opts.headline ??
        (opts.intentLabel ? `Sign in to ${opts.intentLabel}` : undefined);

      openAuthModal({ headline });
    },
    [user, openAuthModal]
  );
}

export interface PendingAuthIntent {
  intentKey: string | null;
  intentLabel: string | null;
  payload: unknown;
  route: string;
  ts: number;
}

/** Read the pending intent without consuming it. Returns null if there
 *  isn't one, it's stale (>10 min), or the user has navigated away from
 *  the route it was stashed on. Does NOT clear the slot. */
function readPendingAuthIntent(): PendingAuthIntent | null {
  try {
    const raw = localStorage.getItem(PENDING_INTENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PendingAuthIntent;
    const age = Date.now() - (parsed.ts || 0);
    // 30 min: a user may take several minutes to open the magic-link email;
    // a 10-min window silently dropped the resume (and the config it carries).
    if (age > 30 * 60 * 1000) return null;
    const here = window.location.pathname + window.location.search;
    if (parsed.route && parsed.route !== here) return null;
    return parsed;
  } catch {
    return null;
  }
}

/**
 * Consume the pending intent. Returns null if there isn't a valid one or
 * (when `predicate` is given) the stored intent isn't the caller's.
 *
 * The slot is cleared ONLY when this call actually takes ownership:
 *   - no predicate  → behaves like before (take/clear any valid intent, and
 *     also sweep away a stale/foreign-route slot so it doesn't linger);
 *   - with predicate → clear+return only on a match, otherwise leave the
 *     intent in place for the listener it belongs to.
 *
 * This lets multiple resume listeners (Follow, Generate) coexist on the same
 * page without the first one to run swallowing another's intent — the bug
 * where clicking Follow while signed out, then signing in, silently dropped
 * the follow because SummariesSection consumed (and discarded) the intent.
 */
export function consumePendingAuthIntent(
  predicate?: (intent: PendingAuthIntent) => boolean
): PendingAuthIntent | null {
  const parsed = readPendingAuthIntent();
  if (!parsed) {
    if (!predicate) {
      try { localStorage.removeItem(PENDING_INTENT_KEY); } catch { /* ignore */ }
    }
    return null;
  }
  if (predicate && !predicate(parsed)) {
    return null; // not ours — leave it for the right listener
  }
  try { localStorage.removeItem(PENDING_INTENT_KEY); } catch { /* ignore */ }
  return parsed;
}
